// src/pages/help/helpUi.js
// Briques visuelles partagées entre le hub du centre d'aide et les pages guide.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, ChevronDown, Copy, HelpCircle, LifeBuoy, Mail, Phone, X } from "lucide-react";

export const SUPPORT_WHATSAPP = "+33652395381";
export const SUPPORT_EMAIL = "support@fullmargin.net";

export const cn = (...a) => a.filter(Boolean).join(" ");

export function WhatsAppLogo({ className = "", size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" className={className} aria-hidden="true">
      <path
        fill="currentColor"
        d="M19.11 17.62c-.25-.12-1.45-.71-1.67-.79-.22-.08-.38-.12-.54.12-.16.25-.62.79-.76.95-.14.16-.28.19-.52.06-.25-.12-1.03-.38-1.97-1.22-.73-.65-1.22-1.45-1.36-1.69-.14-.25-.02-.38.11-.5.11-.11.25-.28.38-.42.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.44-.06-.12-.54-1.31-.74-1.79-.2-.48-.4-.41-.54-.41-.14 0-.31-.02-.48-.02-.16 0-.44.06-.67.31-.23.25-.88.86-.88 2.1 0 1.24.9 2.44 1.02 2.61.12.16 1.77 2.7 4.29 3.79.6.26 1.07.42 1.44.54.61.19 1.16.16 1.6.1.49-.07 1.45-.59 1.65-1.16.2-.57.2-1.06.14-1.16-.06-.1-.22-.16-.47-.28z"
      />
      <path
        fill="currentColor"
        d="M26.67 15.97c0 5.91-4.79 10.7-10.7 10.7-1.88 0-3.72-.5-5.34-1.45L5.33 26.67l1.48-5.12a10.62 10.62 0 0 1-1.54-5.58C5.27 10.06 10.06 5.27 15.97 5.27c2.86 0 5.55 1.11 7.57 3.13 2.02 2.02 3.13 4.71 3.13 7.57zm-10.7-8.92a8.92 8.92 0 0 0-7.72 13.4l-.45.79-.87 3.01 3.09-.82.77-.49a8.87 8.87 0 0 0 4.18 1.04 8.92 8.92 0 1 0 0-17.93z"
      />
    </svg>
  );
}

export function buildWhatsAppHref(rawNumber, prefillText) {
  const digits = String(rawNumber || "").replace(/[^\d]/g, "");
  if (digits.length < 10 || digits.length > 15) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(prefillText || "")}`;
}

export function buildMailtoHref(email, subject, body) {
  if (!email) return null;
  return `mailto:${email}?subject=${encodeURIComponent(subject || "")}&body=${encodeURIComponent(body || "")}`;
}

/* ---------------------------------- Fond ---------------------------------- */
// Dégradé doux façon maquette : violet en haut, rose léger à droite.
export function HelpBg() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 dark:hidden bg-[radial-gradient(75%_55%_at_60%_-5%,rgba(139,92,246,0.16),rgba(255,255,255,0)_60%)]" />
      <div className="pointer-events-none absolute inset-0 dark:hidden bg-[radial-gradient(45%_40%_at_100%_10%,rgba(236,72,153,0.10),rgba(255,255,255,0)_65%)]" />
      <div className="pointer-events-none absolute inset-0 hidden dark:block bg-[radial-gradient(75%_55%_at_60%_-5%,rgba(139,92,246,0.22),rgba(0,0,0,0)_60%)]" />
      <div className="pointer-events-none absolute inset-0 hidden dark:block bg-[radial-gradient(45%_40%_at_100%_10%,rgba(236,72,153,0.14),rgba(0,0,0,0)_65%)]" />
    </>
  );
}

/* --------------------------------- Atomes --------------------------------- */
export const Card = ({ className, children, ...rest }) => (
  <div
    {...rest}
    className={cn(
      "rounded-2xl border border-black/[0.07] bg-white",
      "dark:border-white/10 dark:bg-white/[0.04]",
      className
    )}
  >
    {children}
  </div>
);

export function SearchField({ value, onChange, placeholder, actionLabel, onSubmit }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.();
      }}
      className="flex items-center gap-2 rounded-2xl border border-black/[0.07] bg-white p-1.5 pl-4 dark:border-white/10 dark:bg-white/[0.05]"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0 text-neutral-400" aria-hidden="true">
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
        <path d="m20 20-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent py-2 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 dark:text-white dark:placeholder:text-white/40"
      />
      <button
        type="submit"
        className="shrink-0 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-700"
      >
        {actionLabel}
      </button>
    </form>
  );
}

export function CopyButton({ value, labelCopy, labelCopied, icon: Icon = Copy }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  const doCopy = useCallback(async () => {
    try {
      const v = String(value ?? "");
      if (!v) return;
      if (navigator?.clipboard?.writeText) await navigator.clipboard.writeText(v);
      else {
        const ta = document.createElement("textarea");
        ta.value = v;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1200);
    } catch {}
  }, [value]);

  const Ico = copied ? Check : Icon;

  return (
    <button
      type="button"
      onClick={doCopy}
      title={copied ? labelCopied : labelCopy}
      className={cn(
        "inline-flex items-center gap-2 rounded-xl border border-black/[0.07] bg-white px-3 py-2 text-xs font-semibold transition",
        "hover:bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.05] dark:hover:bg-white/[0.08]",
        copied ? "text-emerald-600 dark:text-emerald-300" : "text-neutral-700 dark:text-white/80"
      )}
    >
      <Ico size={14} />
      {copied ? labelCopied : labelCopy}
    </button>
  );
}

// Accordéon FAQ : ligne fine, chevron à droite, réponse en texte pré-formaté.
export function FaqRow({ id, question, answer, open, onToggle }) {
  const reduce = useReducedMotion();

  return (
    <div className="border-b border-black/[0.06] last:border-b-0 dark:border-white/[0.07]">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
      >
        <HelpCircle size={16} className="shrink-0 text-violet-500 dark:text-violet-300" />
        <span className="min-w-0 flex-1 text-sm font-medium text-neutral-800 dark:text-white/90">{question}</span>
        <ChevronDown
          size={16}
          className={cn("shrink-0 text-neutral-400 transition-transform", open && "rotate-180")}
        />
      </button>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="overflow-hidden"
          >
            {/* Réponse alignée à gauche : pas d'indentation par l'icône. */}
            <div className="whitespace-pre-line px-4 pb-4 text-sm leading-relaxed text-neutral-600 dark:text-white/70">
              {answer}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------- Bouton flottant assistance ------------------------- */
// Bouton flottant « Assistance technique ». Pilotable de l'extérieur (open /
// onOpenChange) pour qu'un bouton de la page puisse ouvrir la même pop-up.
export function SupportFab({ t, open: openProp, onOpenChange }) {
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = useCallback(
    (next) => {
      const value = typeof next === "function" ? next(open) : next;
      if (!controlled) setOpenState(value);
      onOpenChange?.(value);
    },
    [open, controlled, onOpenChange]
  );
  const boxRef = useRef(null);

  const waHref = buildWhatsAppHref(SUPPORT_WHATSAPP, t("support.prefillWhatsApp"));
  const mailHref = buildMailtoHref(SUPPORT_EMAIL, t("support.subjectEmail"), t("support.prefillEmail"));

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown);
    };
  }, [open, setOpen]);

  return (
    <div className="fixed bottom-5 right-5 z-50" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("support.title")}
        aria-expanded={open}
        className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-rose-500 to-red-600 text-white transition hover:brightness-110 active:scale-[0.98]"
      >
        {open ? <X size={20} /> : <HelpCircle size={20} />}
      </button>

      <AnimatePresence>
        {open ? (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              role="dialog"
              aria-modal="true"
              className={cn(
                "fixed z-[61] flex flex-col overflow-hidden bg-white ring-1 ring-black/10 dark:bg-[#0d0a1f] dark:ring-white/10",
                "inset-x-0 bottom-0 max-h-[85vh] rounded-t-3xl pb-[env(safe-area-inset-bottom)]",
                "sm:inset-auto sm:bottom-20 sm:right-5 sm:w-[380px] sm:rounded-2xl sm:pb-0"
              )}
            >
              <div className="flex items-center justify-between gap-3 border-b border-black/[0.07] px-4 py-4 dark:border-white/10">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-violet-500/12 text-violet-600 dark:text-violet-300">
                    <LifeBuoy size={18} />
                  </span>
                  <div className="truncate text-base font-semibold text-neutral-900 dark:text-white">
                    {t("support.title")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label={t("labels.close")}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-neutral-500 transition hover:bg-black/5 dark:text-white/70 dark:hover:bg-white/10"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5">
                <p className="text-sm leading-relaxed text-neutral-600 dark:text-white/70">{t("support.note")}</p>

                <div className="mt-4 space-y-2.5">
                  <a
                    href={waHref || "#"}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => !waHref && e.preventDefault()}
                    className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3.5 text-sm font-semibold text-white transition hover:brightness-110"
                  >
                    <WhatsAppLogo className="text-white" size={18} />
                    {t("support.ctaWhatsApp")}
                  </a>
                  <a
                    href={mailHref || "#"}
                    onClick={(e) => !mailHref && e.preventDefault()}
                    className="flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-3.5 text-sm font-semibold text-white transition hover:brightness-110"
                  >
                    <Mail size={18} />
                    {t("support.ctaEmail")}
                  </a>
                  <div className="grid gap-2 pt-1">
                    <CopyButton value={SUPPORT_EMAIL} labelCopy={t("support.copyEmail")} labelCopied={t("labels.copied")} icon={Mail} />
                    <CopyButton value={SUPPORT_WHATSAPP} labelCopy={t("support.copyNumber")} labelCopied={t("labels.copied")} icon={Phone} />
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
